import React, { useRef, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import NextLazyImage from "@/components/Core/NextLazyImage";
import CtaRenderer from "@/components/Shared/CtaRenderer";
import { render } from "storyblok-rich-text-react-renderer";
import { animate, stagger, onScroll, createSpring, createScope } from "animejs";
import mq from "@/utils/mq";
import ContentWrapper from "@/components/Core/ContentWrapper";

// Outer wrapper
const TeamCtaWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  padding-top: 86px;
  position: relative;
`;

// Grid container
const ImagesWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 1rem;
  max-width: 1136px;
  position: relative;
  width: 100%;
  overflow: hidden;
  /* Only initialize children */
  .team-title,
  .team-description,
  .team-cta-wrapper {
    opacity: 0;
    transform: translateY(20px);
  }

  .team-avatar-image {
    opacity: 0;
    transform: translateY(20px);
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      180deg,
      rgba(8, 9, 10, 0) 6.52%,
      rgba(8, 9, 10, 0.6) 44.56%,
      rgba(8, 9, 10, 0.86) 65.49%,
      #08090a 100%
    );
    pointer-events: none;
  }
  ${mq.mobile(css`
    grid-template-columns: repeat(4, 1fr);
    &::after {
      background: linear-gradient(
        180deg,
        rgba(8, 9, 10, 0) 42.52%,
        rgba(8, 9, 10, 0.6) 64.56%,
        rgba(8, 9, 10, 0.86) 85.49%,
        #08090a 100%
      );
    }
  `)};
`;

// Individual avatar container
const ImageObject = styled.div.attrs({ className: "team-avatar-image" })`
  width: 100%;
  aspect-ratio: 2/3;

  position: relative;
  background: #08090a;
`;

// Text overlay container
const TextContent = styled.div.attrs({ className: "team-text-content" })`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  z-index: 2;
`;

// Styled Title with original styles + className
const Title = styled.h3.attrs({ className: "team-title" })`
  color: #f7f8f8;
  text-align: center;
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family: Inter;
  font-size: 40px;
  font-style: normal;
  font-weight: 500;
  line-height: 44px;
  max-width: 532px;
  ${mq.mobile(css`
    font-size: 32px;
    line-height: 44px;
  `)};
`;

// Styled Description with original styles + className
const Description = styled.div.attrs({ className: "team-description" })`
  color: #b7bac1;
  text-align: center;
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family: Inter;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 23px;
  max-width: 522px;
  ${mq.mobile(css`
    font-size: 16px;
    line-height: 24px;
  `)};
`;

// CTA wrapper styled + className
const CtaWrapper = styled.div.attrs({ className: "team-cta-wrapper" })`
  display: flex;
  align-items: flex-start;
  width: 100%;
  justify-content: center;
`;

const AvatarObject = ({ avatar }: { avatar: any }) => (
  <ImageObject>
    <NextLazyImage
      src={avatar.filename}
      alt={avatar.alt}
      fill
      containerStyles={{ background: "#08090A" }}
      quality={100}
    />
  </ImageObject>
);

const AnimatedTextContent = ({ title, description, ctas }: { title: string; description: any; ctas: any }) => (
  <TextContent>
    <Title>{title}</Title>
    <Description>{render(description)}</Description>
    <CtaWrapper>
      <CtaRenderer ctas={ctas} />
    </CtaWrapper>
  </TextContent>
);

const AnimatedAvatars = ({ avatars }: { avatars: any[] }) =>
  avatars.map((av, i) => <AvatarObject key={av.id || i} avatar={av} />);

export default function TeamCtaGrid({
  title,
  description,
  ctas,
  avatars,
}: {
  title: string;
  description: any;
  ctas: any;
  avatars: any[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState<boolean | null>(null);

  const MOBILE_BREAKPOINT = 786;

  useEffect(() => {
    // Function to check if we should use mobile layout
    const checkMobile = () => {
      const newIsMobile = window.innerWidth < MOBILE_BREAKPOINT;
      // Only update state if it changed or is initial (null)
      if (isMobile === null || newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
      }
    };

    // Check initially
    checkMobile();

    // Use resize observer instead of resize event for better performance
    // It still may fire often but our conditional update prevents re-renders
    const resizeObserver = new ResizeObserver(() => {
      checkMobile();
    });

    resizeObserver.observe(document.body);

    // Cleanup
    return () => resizeObserver.disconnect();
  }, [isMobile]);

  const debug = false;

  // Limit avatars to 16 for mobile screens
  const displayedAvatars = isMobile ? avatars.slice(0, 12) : avatars;

  useEffect(() => {
    if (!containerRef.current) return;

    const scope = createScope({ root: containerRef });
    scope.add(() => {
      animate(".team-title, .team-description, .team-cta-wrapper", {
        opacity: [0, 1],
        translateY: [20, 0],
        ease: createSpring({ stiffness: 150, damping: 20 }),
        delay: stagger(300),
        autoplay: onScroll({
          target: ".team-text-content",
          axis: "y",
          enter: "bottom+=10% bottom",
          leave: "center-=30% center",
          sync: 0.25,
          debug: debug,
        }),
      });

      animate(".team-avatar-image", {
        opacity: [0, 1],
        translateY: [20, 0],
        ease: createSpring({ stiffness: 150, damping: 20 }),
        delay: stagger(100),
        autoplay: onScroll({
          target: ".team-avatar-image",
          axis: "y",
          enter: "bottom+=160 bottom",
          leave: "center-=25% center",
          sync: 0.2,
          debug: debug,
        }),
      });
    });

    return () => scope.revert();
  }, [displayedAvatars]);

  return (
    <ContentWrapper transparent>
      <TeamCtaWrapper ref={containerRef}>
        <AnimatedTextContent title={title} description={description} ctas={ctas} />
        <ImagesWrapper>
          <AnimatedAvatars avatars={displayedAvatars} />
        </ImagesWrapper>
      </TeamCtaWrapper>
    </ContentWrapper>
  );
}
